import { api, unwrap, cleanParams } from "../lib/axios";
import { API_BASE_URL } from "../config/env";
import { tokenStore } from "../lib/tokenStore";
import type { ApiEnvelope } from "../types/api";

export type ReportPreset =
  | "today"
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "this_year"
  | "last_year"
  | "custom";

// `type` (not `interface`) so it satisfies cleanParams' Record<string, unknown>.
export type ReportParams = {
  preset?: ReportPreset;
  startDate?: string;
  endDate?: string;
  limit?: number;
};

// The report endpoints under /admin/reports/* — JSON variants.
const REPORTS = {
  financialDashboard: "financial-dashboard",
  revenueSummary: "revenue-summary",
  dailyRevenue: "daily-revenue",
  paymentDistribution: "payment-distribution",
  transactionStatus: "transaction-status",
  gatewayFees: "gateway-fees",
  driverEarnings: "driver-earnings",
  driverDebt: "driver-debt",
  couponImpact: "coupon-impact",
} as const;

export type ReportKey = keyof typeof REPORTS;

export const reportsApi = {
  async get<T = Record<string, unknown>>(
    key: ReportKey,
    params: ReportParams,
  ): Promise<T> {
    const res = await api.get<ApiEnvelope<T>>(
      `/admin/reports/${REPORTS[key]}`,
      { params: cleanParams(params) },
    );
    return unwrap(res);
  },

  /**
   * Download a report's CSV. Plain <a download> can't send the Bearer header,
   * so we fetch with auth, build a Blob, and trigger a client-side download.
   * (Only the 8 report keys that expose a /csv variant — couponImpact included.)
   */
  async downloadCsv(key: ReportKey, params: ReportParams): Promise<void> {
    const qs = new URLSearchParams(
      Object.entries(cleanParams(params)).map(([k, v]) => [k, String(v)]),
    ).toString();
    const url = `${API_BASE_URL}/admin/reports/${REPORTS[key]}/csv${
      qs ? `?${qs}` : ""
    }`;

    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${tokenStore.getAccess() ?? ""}` },
    });
    if (!res.ok) throw new Error(`CSV download failed (${res.status})`);

    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = `safiri-${REPORTS[key]}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  },
};
